<script lang="ts">
  import { blogFilter } from "$stores";
  import { ButtonColor, IconType } from "$types/atoms";
  import Button from "$lib/atoms/Button.svelte";
  import { parseUrl } from "$lib/utils/helpers";
  import resizeImage from "$lib/utils/resizeImage";
  import { aspectRatio } from "$lib/utils/optimizeImage";
  import { getContext } from "svelte";
  import type { NewsroomListStoryblok } from "$types/components";
  import type { PostsResponse } from "$types/data";

  type Post = PostsResponse["data"]["stories"][number];

  export let blok: NewsroomListStoryblok;
  export let post: Post;

  const isExternalLink = (post?.content?.externalLink?.url?.length ?? 0) > 0;

  const datasources = getContext("datasources") as {
    topics: { name: string; value: string }[];
    industries: { name: string; value: string }[];
  };

  function categoryName(category: string) {
    return (
      datasources.industries.find((candidate) => candidate.value === category)
        ?.name ?? ""
    );
  }

  function topicName(topic: string) {
    return (
      datasources.topics.find((candidate) => candidate.value === topic)?.name ??
      ""
    );
  }

  function formatCategory(blok: NewsroomListStoryblok, post: Post): string {
    if (!post?.content?.industries) {
      return "";
    }

    return blok.categoryFormat === "normal" &&
      post.content.industries.length === 1 &&
      !($blogFilter?.filter ?? "")
      ? post.content.industries[0]
      : "";
  }

  function formatDate(blok: NewsroomListStoryblok, post: Post) {
    switch (blok.dateFormat || "none") {
      case "long":
        return post.post_date;
      case "short":
        return post.post_date_short;
      case "terse":
        return post.post_date_terse;
      case "none":
      default:
        return "";
    }
  }

  const topics = post?.content?.topics ?? [];
  const hasFilterTopic = topics.find((topic) => $blogFilter?.filter === topic);
  const nonFilterTopics = topics.filter(
    (topic) => $blogFilter?.filter !== topic,
  );

  $: maxTopics = blok.maxTopics ? Number(blok.maxTopics) : 0;
  $: maxColumns = blok.maxColumns ? Number(blok.maxColumns) : 4;
  $: displayTopics = hasFilterTopic
    ? [$blogFilter?.filter, nonFilterTopics?.[0]]
    : topics.slice(0, maxTopics);
  $: displayCategory = formatCategory(blok, post);
  $: displayDate = formatDate(blok, post);
</script>

{#if post && post.full_slug}
  <a
    href={post?.content?.externalLink?.url || "/" + parseUrl(post.full_slug)}
    target={isExternalLink ? "_blank" : ""}
    rel={isExternalLink ? "noopener noreferrer" : ""}
    class={`
      flex
      flex-col
      no-underline
      group
      rounded-[var(--border-radius-wrapper)]
      bg-[rgb(var(--color-warm-grey))]
      overflow-hidden
    `}
    class:p-4={blok.insetImage ?? false}
  >
    <div
      class={`
        aspect-[384/202]
        object-cover
        object-center
        overflow-hidden
        group-hover:opacity-90
        transition-opacity duration-300
        ${
          post?.content?.mastheadImage?.filename
            ? ""
            : "bg-[rgb(var(--color-yellow-primary))]"
        }
      `}
    >
      {#if post?.content?.mastheadImage?.filename}
        <img
          class="w-full h-full object-cover"
          class:rounded-md={blok.insetImage ?? false}
          src={resizeImage(post.content.mastheadImage.filename)}
          alt={post.content.mastheadImage.alt}
          style={aspectRatio(post.content.mastheadImage.filename)}
          loading="lazy"
        />
      {/if}
    </div>

    <div
      class="py-[16px] px-[16px] {blok.insetImage
        ? 'pb-2'
        : ''} flex flex-col flex-1 md:min-h-[140px]"
    >
      <div
        class="flex flex-col flex-1 gap-[16px]"
        class:lg:gap-[24px]={!blok.fluidHeadingSize}
        class:lg:gap-[32px]={blok.fluidHeadingSize}
        class:justify-between={!blok.topAlignTeaser}
      >
        <div class="space-y-[16px]">
          {#if displayDate || displayCategory}
            <div class="flex justify-between">
              {#if displayDate}
                <div class="type:h5-eyebrow text-charcoal/50">
                  {displayDate}
                </div>
              {/if}
              {#if displayCategory}
                <h4 class="type:h5-eyebrow text-charcoal/50">
                  {categoryName(displayCategory)}
                </h4>
              {/if}
            </div>
          {/if}
          <h4
            class="type:h4-small text-charcoal line-clamp-3 mt-0"
            class:lg:text-[32px]={maxColumns === 2}
            class:lg:leading-[1.1]={maxColumns === 2}
            class:xl:text-[48px]={maxColumns === 2}
          >
            <span class="hover:underline">
              {@html post?.content?.previewHeading || post.name}
            </span>
          </h4>
        </div>
        <p
          class="type:body-small text-[#4B4B4B] line-clamp-1"
          class:line-clamp-1={blok.teaserFormat === "normal"}
          class:line-clamp-2={blok.teaserFormat === "two-line"}
          class:line-clamp-3={blok.teaserFormat === "three-line"}
          class:bottomPaddingMedium={blok.cardBottomPadding === "medium"}
        >
          {#if blok.teaserFormat !== "none" && (post?.content?.previewTeaser?.length ?? 0) > 0}
            {post.content.previewTeaser}
          {/if}
        </p>
        {#if blok.buttonType === "cta"}
          <p>cta {blok.buttonText}</p>
        {:else if blok.buttonType === "arrow"}
          <Button
            class="button"
            color={ButtonColor.GRAY}
            icon={IconType.ARROW_LINK}
            tiny
            textOutside
            label={blok.buttonText}
          >
            <span class="type:h5 noresize text-charcoal">{blok.buttonText}</span
            >
          </Button>
        {/if}

        {#if maxTopics > 0 && displayTopics.length > 0}
          <ul class="topics flex flex-row gap-[8px] type:label text-charcoal">
            {#each displayTopics as topic}
              {#if topic && topicName(topic)}
                <li class="whitespace-nowrap py-[8px] px-[12px]">
                  {topicName(topic)}
                </li>
              {/if}
            {/each}
          </ul>
        {/if}
      </div>
    </div>
  </a>
{/if}

<style lang="postcss">
  .bottomPaddingMedium {
    @screen md {
      padding-bottom: 16px;
    }
  }
  .topics li {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 40px;
  }
</style>
